import { BuildingOfficeIcon, MapPinIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import type { Company } from '../types/company.types';

interface SubscriptionInfo {
  planName: string | null;
  status: string;
  isActive: boolean;
}

interface CompanyCardProps {
  company: Company;
  isSelected?: boolean;
  isSelecting?: boolean;
  subscriptionInfo?: SubscriptionInfo;
  onSelect: (companyId: string) => void;
}

export function CompanyCard({ company, isSelected, isSelecting, subscriptionInfo, onSelect }: CompanyCardProps) {
  return (
    <button
      onClick={() => onSelect(company.id)}
      disabled={isSelecting}
      className={`
        relative w-full p-6 rounded-lg border-2 transition-all duration-200
        hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        ${
          isSelected
            ? 'border-primary-500 bg-primary-50 shadow-md'
            : 'border-gray-200 bg-white hover:border-primary-300'
        }
        ${isSelecting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute top-4 right-4">
          <CheckCircleIcon className="h-6 w-6 text-primary-600" />
        </div>
      )}

      {/* Company Logo */}
      <div className="flex items-start space-x-4">
        {company.image ? (
          <img
            src={company.image}
            alt={company.name}
            className="h-16 w-16 rounded-lg object-cover"
          />
        ) : (
          <div className="h-16 w-16 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
            <BuildingOfficeIcon className="h-8 w-8 text-white" />
          </div>
        )}

        <div className="flex-1 text-left">
          {/* Company Name */}
          <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>

          {/* RUC */}
          {company.ruc && (
            <p className="text-sm text-gray-600 mt-1">
              RUC: <span className="font-mono">{company.ruc}</span>
            </p>
          )}

          {/* Code */}
          {company.code && (
            <p className="text-xs text-gray-500 mt-1">
              Código: <span className="font-mono">{company.code}</span>
            </p>
          )}

          {/* Plan Badge */}
          {subscriptionInfo && (
            <div className="mt-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                subscriptionInfo.isActive
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {subscriptionInfo.planName || 'Sin Plan'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Additional Info */}
      <div className="mt-4 space-y-2">
        {company.address && (
          <div className="flex items-start text-sm text-gray-600">
            <MapPinIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-2">{company.address}</span>
          </div>
        )}

        {company.phone && (
          <div className="flex items-center text-sm text-gray-600">
            <PhoneIcon className="h-4 w-4 mr-2 flex-shrink-0" />
            <span>{company.phone}</span>
          </div>
        )}

        {company.email && (
          <div className="flex items-center text-sm text-gray-600">
            <EnvelopeIcon className="h-4 w-4 mr-2 flex-shrink-0" />
            <span className="truncate">{company.email}</span>
          </div>
        )}
      </div>

      {/* Select Button Overlay */}
      {!isSelected && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <span className="text-sm font-medium text-primary-600">Click para seleccionar</span>
        </div>
      )}

      {isSelected && (
        <div className="mt-4 pt-4 border-t border-primary-200">
          <span className="text-sm font-medium text-primary-700">Empresa Seleccionada</span>
        </div>
      )}
    </button>
  );
}
